import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Upload } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { nanoid } from "nanoid";
import { toast } from "react-toastify";
import { showSuccess, showError } from "../../utils/toast";
import { useForm, Controller } from "react-hook-form";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AiFillCloseCircle } from "react-icons/ai";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputUI from "../material-ui/InputUI";
import ButtonUI from "../material-ui/ButtonUI";
import { AuthContext } from "../../context/Auth/AuthContext";
import addDocument from "../../api/addDocument";
import addAdditionalDocument from "../../api/addAdditionalDocument";
import "../../styles/AddDocument.css";
import baseUrl, { sugarUrl } from "../../config/baseUrl";
import addDoc from "../../images/add-document.png";
import clientsData from "../../api/clients";
import useDebounceSearch from "../../hooks/useDebounceSearch";
import elasticToken from "../../config/elasticToken";
// get-client-reference-number no longer called - client validity/reference number
// now comes from getCaseStatus (see handleClientNameChange below).
// import getClientRefNo from "../../api/getClientRefNo";
import Loading from "../material-ui/Loading";
import { BiLinkAlt } from "react-icons/bi";
import getAllFieldTypeList from "../../api/getAllFieldTypeList";

const SUPPORTED_FORMATS = [
  "image/jpg",
  "image/jpeg",
  "image/gif",
  "image/png",
  "image/bmp",
  "text/plain",
  "application/pdf",
  "application/zip",
  "application/x-zip-compressed", // sometimes zip mime
  "application/vnd.rar",
  "application/vnd.ms-excel", // xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
  "application/msword", // doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
  "application/vnd.ms-powerpoint", // ppt
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
  "application/rtf", // rtf
  "text/rtf", // sometimes rtf mime
  "video/mp4", // mp4
  "video/quicktime", // mov
  "video/x-ms-wmv", // wmv
  "audio/mp3", // mp3
  "audio/aac", // aac
  "audio/x-aac", // aac (alternate)
  "audio/amr", // amr
  "audio/opus", // opus
  "audio/mpeg", // mp3 / mp1 / mp2 / mpa / mpeg (audio)
  "audio/mp4", // m4a
  "audio/x-m4a", // m4a (alternate)
  "image/tiff",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/x-icon",
  "text/csv",
  "text/html",
  "application/x-rar-compressed",
  "application/octet-stream",
  "application/onenote",
  "application/x-onenote",
  "application/ms-onenote",
  // emails / calendar / web archive / outlook msg
  "message/rfc822", // .eml
  "text/calendar", // .ics
  "image/pjpeg", // .jfif sometimes reported as pjpeg
  "application/x-mimearchive", // .mhtml
  "application/vnd.apple.pages", // .pages
  "application/x-iwork-pages-sffpages", // older .pages
  "application/internet-shortcut", // .url
  "application/x-mswinurl", // .url (alt)
  "text/vcard", // .vcf
  "text/x-vcard", // .vcf (alt)
  "application/vcard", // .vcf (alt)
  "video/3gpp", // .3gp (video)
  "audio/3gpp", // .3gp (audio variant)
  "application/vnd.ms-outlook", // .msg
  "application/x-msg", // .msg (alt)
  // newly added audio
  "audio/wav", // wav
  "audio/x-wav", // wav (alternate)
  "audio/wave", // wav (alternate)
  "audio/aiff", // aiff
  "audio/x-aiff", // aiff (alternate)
  "audio/flac", // flac
  "audio/x-flac", // flac (alternate)
  "audio/alac", // alac
  "audio/x-alac", // alac (alternate)
  "audio/ogg", // ogg
  "audio/vorbis", // ogg (alternate)
  "application/ogg", // ogg (alternate)
  "audio/x-ms-wma", // wma
  "audio/wma", // wma (alternate)
  "audio/midi", // midi
  "audio/x-midi", // midi (alternate)
  "audio/mp1", // mp1
  "audio/mp2", // mp2
  "audio/mpa", // mpa
  "video/mpeg", // mpeg / mpg
];

// Extensions to expose in chooser when MIME is empty/unknown
const ADDITIONAL_ACCEPT_EXTS = [
  ".one",
  ".eml",
  ".ics",
  ".jfif",
  ".mhtml",
  ".pages",
  ".url",
  ".vcf",
  ".3gp",
  ".msg",
  ".wmv",
  ".opus",
  ".amr",
  ".crdownload",
  ".bmp",
  ".htm",
  ".html",
  // newly added audio
  ".wav",
  ".aiff",
  ".aif",
  ".flac",
  ".alac",
  ".ogg",
  ".oga",
  ".wma",
  ".midi",
  ".mid",
  ".mp1",
  ".mp2",
  ".mpa",
  ".mpeg",
  ".mpg",
];

// --- helpers for linkable files (matches Upload component behavior)
const getFileExtension = (name = "") =>
  name.split(".").pop()?.toLowerCase() || "";
const isLinkableExt = (ext) =>
  ["pdf", "jpg", "jpeg", "png", "doc", "docx"].includes(ext);

// Normalize extension (treat jpg == jpeg, jfif -> jpeg, htm -> html, tif -> tiff)
const normalizeExt = (ext = "") => {
  const e = (ext || "").toLowerCase();
  if (e === "jpg") return "jpeg";
  if (e === "jfif") return "jpeg";
  if (e === "htm") return "html";
  if (e === "tif") return "tiff";
  if (e === "doc") return "docx";
  if (e === "aif") return "aiff";
  if (e === "oga") return "ogg";
  if (e === "mid") return "midi";
  if (e === "mpg") return "mpeg";
  return e;
};

// One canonical extension regex (used by onDrop + validation)
const ALLOWED_EXT_REGEX =
  /\.(jpg|jpeg|jfif|gif|png|bmp|pdf|xlsx|xls|zip|doc|docx|rar|ppt|pptx|txt|rtf|mp4|mov|wmv|mp3|aac|amr|opus|tiff|tif|webp|heic|heif|ico|csv|html|htm|bin|one|m4a|eml|ics|mhtml|pages|url|vcf|3gp|msg|crdownload|wav|aiff|aif|flac|alac|ogg|oga|wma|midi|mid|mp1|mp2|mpa|mpeg|mpg)$/i;

const AddDocument = ({
  onAddDocumentButtonClick,
  setDocumentAddedToTrue,
  //categoryOptions,
  // subCategoryOptions,
  //clientNames,
}) => {
  const [loading, setLoading] = useState(false);
  const [requestOptions, setRequestOptions] = useState([]);
  const { token, user_id, is_admin } = useContext(AuthContext);
  const [linkRequest, setLinkRequest] = useState(false);
  const [clientNames, setClientNames] = useState([]);
  const [searchClientNotFound, setSearchClientNotFound] = useState(false);
  const [files, setFiles] = useState([]);
  const [benefId, setBenefId] = useState("");
  const [fileError, setFileError] = useState(true);
  const [openSubCategory, setOpenSubCategory] = useState(false);
  const [searchedTerm, setSearchedTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [subCategoryLoading, setSubCategoryLoading] = useState(false);
  // const loadingSubCategory = openSubCategory && subCategoryOptions.length === 0;
  const [clientNameLabel, setClientNameLabel] = useState("");
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [showMoreLoading, setShowMoreLoading] = useState(false);
  const [searchedClientId, setSearchedClientId] = useState([]);
  const [additionalDataFetched, setAdditionalDataFetched] = useState(false);
  const [additionalDataLoading, setAdditionalDataLoading] = useState(false);
  const [isClientListFetched, setIsClientListFetched] = useState(false);
  const [sugarTokenIssue, setSugarTokenIssue] = useState("");
  const [isFileValidForLinking, setIsFileValidForLinking] = useState(false);
  const MAX_FILE_SIZE = 300 * 1024 * 1024; // 300MB
  const COMPRESSION_THRESHOLD = 5 * 1024 * 1024; // 5MB
  const [hasLargeFile, setHasLargeFile] = useState(false);

  const addDocumentFormSchema = yup.object().shape({
    documentName: yup.string().required("Document Name is required field"),
    // documentDescription: yup
    //   .string()
    //   .required("Document Description is required field"),
    addClientName: yup
      .string()
      .nullable()
      .required("Client Name is required field"),
    // Category: yup.string().required("Category is required field"),
    subCategory: yup.string().required("Sub Category is required field"),
    addRequestName: yup.string(),
    // addDocumentStatus: yup.string().required("Status is required field"),
    fileUpload: yup
      .mixed()
      .test("required", "Please select a file", (value) => {
        return value && value.length > 0;
      })
      .test("type", "Unsupported file type", (value) => {
        const f = value?.[0];
        if (!f) return false;
        // Accept by MIME when present OR by filename extension
        const byMime = !!f.type && SUPPORTED_FORMATS.includes(f.type);
        const byExt = ALLOWED_EXT_REGEX.test(f.name || "");
        return byMime || byExt;
      })
      .test(
        "fileSize",
        "Please upload file having size less than 300MB", // ← updated message
        (value) => {
          setFileError(true);
          return value && value[0]?.size <= MAX_FILE_SIZE; // ← use constant
        },
      ),
  });
  // Selected file's normalized extension ('' if no file)
  const fileExtension = useMemo(() => {
    const f = files?.[0];
    return normalizeExt(f ? getFileExtension(f.name) : "");
  }, [files]);

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 1) {
      setFileError(true);
    }

    let containsLargeFile = false; // ← add

    const filteredFiles = acceptedFiles.filter((file) => {
      if (!ALLOWED_EXT_REGEX.test(file.name)) return false;
      if (file.size > MAX_FILE_SIZE) {
        // ← add size check
        showError(`${file.name} exceeds 300MB limit`);
        return false;
      }
      if (file.size > COMPRESSION_THRESHOLD) containsLargeFile = true; // ← add
      return true;
    });

    setHasLargeFile(containsLargeFile); // ← add

    const unsupportedFiles = acceptedFiles.filter(
      (file) => !ALLOWED_EXT_REGEX.test(file.name),
    );

    if (unsupportedFiles.length > 0) {
      setFileError(true);
    } else {
      setFileError(false);
    }

    setFiles(filteredFiles);
    setValue("fileUpload", filteredFiles);
  };
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    // UPDATED: include additional extensions explicitly (so chooser shows them & DnD accepts them)
    accept: [...SUPPORTED_FORMATS, ...ADDITIONAL_ACCEPT_EXTS].join(","),
  });

  const fetchSubCategory = async () => {
    setSubCategoryLoading(true);
    try {
      const response = await getAllFieldTypeList({
        token,
        type: "document_subtype",
      });

      // make sure we always store an array
      const list = Array.isArray(response?.data) ? response.data : [];
      setSubCategoryOptions(list);
    } catch (error) {
      console.error("Error fetching sub categories:", error);
      setSubCategoryOptions([]); // keep it safe
    } finally {
      setSubCategoryLoading(false);
    }
  };
  useEffect(() => {
    fetchSubCategory();
  }, []);
  const loadingSubCategory = openSubCategory && subCategoryLoading;
  useEffect(() => {
    setIsClientListFetched(false);
    if (clientNames.length > 0) {
      setSearchedClientId(
        clientNames.map((client) => {
          if (client._module === "Contacts") {
            return client.case_id;
          }

          if (client._module === "cpm_CaseParties") {
            return client.id;   // or linked_case_id if your API provides one
          }

          return client.id;
          // return client.id; // Use id for other modules
        }),
      );
      setIsClientListFetched(true);
    }
  }, [clientNames]);

  const clientIdsString = searchedClientId.join(",");

  // Fetches and updates client data (case status and legal team) for the provided client IDs.
  const fetchAdditionalClientData = async () => {
    setAdditionalDataLoading(true);

    if (!isClientListFetched) {
      console.error("No client IDs found to fetch data.");
      setAdditionalDataLoading(false);
      return;
    }

    try {
      const fetchedData = [];
      console.log("clientIdsString:", clientIdsString);
      console.log("clientNames:", clientNames);
      console.log("isClientListFetched:", isClientListFetched);

      const response = await fetch(
        `${baseUrl}/api/getCaseStatus?caseId=${clientIdsString}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (data?.caseDetails) {
        data.caseDetails.forEach((caseDetail) => {
          const { status, Lead_Attorney, Legal_Assistant, Paralegal } =
            caseDetail;

          fetchedData.push({
            status,
            Lead_Attorney,
            Legal_Assistant,
            Paralegal,
          });
        });
      }

      setClientNames((prev) =>
        prev.map((client, index) => ({
          ...client,
          ...(fetchedData[index] || {}),
        })),
      );

      setAdditionalDataLoading(false);
      setAdditionalDataFetched(true);
    } catch (error) {
      setAdditionalDataLoading(false);
      console.error("Error fetching additional client data:", error);
    }
  };

  // Fetches and updates the client list based on the search term and offset.
  const getClientList = useCallback(
    async (searchTerm, offsetValue, contactId) => {
      if (offsetValue === 0) {
        setClientNames([]);
      }
      if (searchTerm) {
        setClientNameLabel("Client Name");
        setSearchLoading(true);
        try {
          const url = `${baseUrl}/api/customGlobalSearch?q=${searchTerm}&max_num=20&offset=${offsetValue}&highlights=true`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const data = await response.json();

          if (data?.total > 0) {
            setClientNames((prev) => [...prev, ...data.records]);
            setTotal(data.total);
            setSearchLoading(false);
            setShowMoreLoading(false);
          } else {
            if (data?.error == "Failed to Search") {
              setSugarTokenIssue(
                "Invalid token for accessing the SugarCRM APIs.",
              );
            } else {
              setSearchClientNotFound(true);
            }
            setSearchLoading(false);
          }
        } catch (error) {
          setSearchLoading(false);
          console.error("Error:", error);
        }

        // const url = `${baseUrl}/api/get-all-client-data?isAdmin=${is_admin}&userId=${user_id}&client_name=${searchTerm}`;
        // const res = await clientsData({ token, api: url });
        // if (res.code === 200) {
        //   setClientNames(res?.data[0]?.clients);
        // } else {
        //   setSearchClientNotFound(true);
        // }
      } else {
        setClientNameLabel(
          "Client Name / Alien Number / Case Id / Phone No / Email",
        );
      }
    },
    [],
  );
  const [searchTerm, handleClientNameSearch] = useDebounceSearch(
    "",
    "",
    offset,
    "",
    2000,
    getClientList,
  );

  // Fetches additional client data when the client list is fetched or client IDs change.
  useEffect(() => {
    if (isClientListFetched || clientIdsString) {
      fetchAdditionalClientData();
    }
  }, [isClientListFetched, clientIdsString]);

  // const statusOptions = [
  //   {
  //     name: "Active",
  //     value: "active",
  //   },
  //   {
  //     name: "Inactive",
  //     value: "inactive",
  //   },
  // ];

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    ignoreCase: true,
    stringify: (option) => option.fields_name,
  });

  const filterOptionsLinkList = createFilterOptions({
    matchFrom: "any",
    ignoreCase: true,
    stringify: (option) => option.document_req_name,
  });

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(addDocumentFormSchema),
  });

  const selectedClient = watch("addClientName");

  const onAddDocumentFormSubmit = async (addDocumentFormData) => {
    setLoading(true);

    if (
      addDocumentFormData.addRequestName &&
      addDocumentFormData.addRequestName != "Additional Document"
    ) {
      const addDocumentFormResponse = await addDocument({
        token: token,
        user_id: user_id,
        contact_id: benefId,
        document_name: addDocumentFormData.documentName,
        document_desc: addDocumentFormData.documentDescription,
        document_request_id: addDocumentFormData.addRequestName,
        category_id: "0c73a993-4dce-4430-b5a9-9d4ea57739e8",
        document_sub_category: addDocumentFormData.subCategory,
        favourite: "0",
        // document_status: addDocumentFormData.addDocumentStatus,
        document_url: files[0],
      });

      if (addDocumentFormResponse?.statusCode === 200) {
        setLoading(false);
        onAddDocumentButtonClick();
        setDocumentAddedToTrue();
        showSuccess("Document is added successfully");
      } else {
        setLoading(false);
        toast.error(
          addDocumentFormResponse?.message || "Error while adding document",
          {
            position: "top-center",
            autoClose: 5000,
            theme: "dark",
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          },
        );
      }
    } else {
      const addAdditionalDocumentFormResponse = await addAdditionalDocument({
        token: token,
        user_id: user_id,
        contact_id: benefId,
        document_name: addDocumentFormData.documentName,
        document_desc: addDocumentFormData.documentDescription,
        category_id: "0c73a993-4dce-4430-b5a9-9d4ea57739e8",
        document_sub_category: addDocumentFormData.subCategory,
        // document_status: addDocumentFormData.addDocumentStatus,
        content_type: "pdf",
        document_url: files[0],
      });

      if (addAdditionalDocumentFormResponse?.statusCode === 200) {
        // const url = `https://filescour.consumerlaw.com/upload`;
        // const token = "29649f8f-4055-4b7e-b5db-c3e57d875d66";

        // const formData = new FormData();
        // formData.append("file", addDocumentFormData.fileUpload[0]);
        // const response = await fetch(url, {
        //   method: "POST",
        //   headers: {
        //     Authorization: `Bearer ${token}`,
        //   },
        //   body: formData,
        // });
        setLoading(false);
        onAddDocumentButtonClick();
        setDocumentAddedToTrue();
        showSuccess("Document is added successfully");
      } else {
        setLoading(false);
        showError("Error while adding document");
      }
    }
  };

  const fetchRequestList = async () => {
    setLinkRequest(false);
    //const url = `${baseUrl}/api/get-doc-request-data?userId=1&isAdmin=1&contact_id=${selectedClient}&unlinked=True`;
    const url = `${baseUrl}/api/get-client-document-requests-for-linking?userId=1&isAdmin=1&contact_id=${benefId}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data?.code === 200) {
        // Assuming data?.data?.document_requests_for_linking is the list of request options
        const documentCases = data?.data?.document_requests_for_linking || [];

        // Add "Additional Document" as the first item in the list
        const updatedOptions = [
          { document_request_id: "", document_req_name: "Additional Document" },
          ...documentCases,
        ];

        setRequestOptions(updatedOptions);
      } else {
        setRequestOptions([
          {
            document_request_id: "",
            document_req_name: "Additional Document",
          },
        ]);
      }
    } catch (error) {
      return error;
    }
  };

  const handleRequestListLink = () => {
    if (selectedClient && filteredRequestOptions?.length > 0) {
      setLinkRequest(true);
    }
  };

  // DMS-local client validity check - uses getCaseStatus instead of a separate
  // get-client-reference-number call. A genuine request failure still surfaces the
  // error toast; a client simply having no reference number set is not treated as
  // an error.
  const handleClientNameChange = async (contactId) => {
    let response;
    let data;
    try {
      response = await fetch(`${baseUrl}/api/getCaseStatus?caseId=${contactId}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      data = await response.json();
    } catch (error) {
      response = null;
    }

    if (!response?.ok || !data?.caseDetails) {
      toast.error(
        "Error while getting client details in DMS. Please contact admin!",
        {
          position: "top-center",
          autoClose: 5000,
          theme: "dark",
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        },
      );
      //setClientNames([]);
      setValue("addClientName", "");
    } else {
      setClientNameLabel("Client Name");
      setValue("addRequestName", "");
      fetchRequestList();
    }
  };

  useEffect(() => {
    if (
      typeof selectedClient !== "undefined" &&
      selectedClient !== null &&
      selectedClient
    ) {
      handleClientNameChange(selectedClient);
    }
    if (!selectedClient) setValue("addClientName", "");
  }, [selectedClient]);

  useEffect(() => {
    setOffset(0);
    setTotal(0);
  }, [searchedTerm]);

  // React to file changes to control linkability and dropdown visibility
  useEffect(() => {
    const f = files?.[0];
    if (f) {
      const ext = getFileExtension(f.name);
      const valid = isLinkableExt(ext);
      setIsFileValidForLinking(valid);
      // if (!valid && linkRequest) {
      //   setLinkRequest(false);
      //   setValue("addRequestName", "");
      // }
    } else {
      setIsFileValidForLinking(false);
      if (linkRequest) setLinkRequest(false);
      setValue("addRequestName", "");
    }
    // intentionally NOT depending on linkRequest to avoid flicker loops
  }, [files, setValue]);

  // Option filtering when file isn't linkable (hide present items; keep Additional Document)
  const filteredRequestOptions = useMemo(() => {
    if (!Array.isArray(requestOptions)) return [];

    const ADDITIONAL_LABEL = "Additional Document";

    return requestOptions.filter((o) => {
      // Always keep the "Additional Document" option
      if (o?.document_req_name === ADDITIONAL_LABEL) return true;

      // Keep anything where is_present !== 1 (i.e., is_present is either 0, undefined, or other values)
      if (o?.is_present !== 1) return true;

      // From here on, only items with is_present === 1

      // If the file isn't valid for linking, exclude present items
      if (!isFileValidForLinking) return false;

      // If file is linkable → require strict document_type match
      if (!fileExtension) return false;
      return normalizeExt(o?.document_type || "") === fileExtension;
    });
  }, [requestOptions, isFileValidForLinking, fileExtension]);

  return (
    <div className="add-document-page">
      <div className="add-document-container">
        <div className="add-document-header">
          <div className="add-document-title-with-img">
            <img src={addDoc} height="100px" width="120px" alt="Add Document" />
            <div className="add-document-title">Add New Document</div>
          </div>

          <ButtonUI
            formButton={false}
            variant="contained"
            buttonText={<AiFillCloseCircle />}
            style={{
              background: "none",
              padding: "0px",
              minWidth: "auto",
            }}
            onClickFunc={onAddDocumentButtonClick}
          ></ButtonUI>
        </div>
        <div className="add-document-form">
          <div className="add-document-inputs">
            <div
              className="add-document-inputs-with-error-message"
              style={{ width: "100%" }}
            >
              <Controller
                control={control}
                name="addClientName"
                render={({ field }) => (
                  <Autocomplete
                    className="clientNameAuto"
                    name="addClientName"
                    sx={{ margin: "10px 0px" }}
                    options={[
                      ...clientNames,
                      ...(total > offset + 20 || showMoreLoading
                        ? [{ name: "Show More", id: "show_more" }]
                        : []),
                    ]}
                    loading={searchLoading}
                    //getOptionLabel={(option) => option.name}
                    getOptionLabel={(option) => {
                      // Check if option is an object and has a valid 'name' property
                      if (
                        typeof option === "object" &&
                        option !== null &&
                        typeof option.name === "string"
                      ) {
                        if (option.id === "show_more") return option.name;
                        return option.name || "";
                      }

                      // Return a fallback value (could be an empty string or some placeholder text)
                      return "";
                    }}
                    noOptionsText={
                      !searchClientNotFound
                        ? sugarTokenIssue
                          ? sugarTokenIssue
                          : "Please enter the client name / alien number / case id / phone no / email"
                        : "No clients found"
                    }
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          !clientNameLabel
                            ? "Client Name / Alien Number / Case Id / Phone No / Email"
                            : clientNameLabel
                        }
                        InputProps={{
                          ...params.InputProps,
                        }}
                        onChange={(event) => {
                          if (!event.target.value) {
                            setClientNameLabel("Client Name / Alien Number");
                          }
                          const searchTerm = event.target.value;
                          handleClientNameSearch(searchTerm);
                          setSearchedTerm(searchTerm);
                        }}
                        onBlur={() => {
                          if (
                            !selectedClient &&
                            typeof selectedClient != "undefined"
                          ) {
                            setClientNameLabel(
                              "Client Name / Alien Number / Case Id / Phone No / Email",
                            );
                          }
                        }}
                      />
                    )}
                    filterOptions={(options, state) => options}
                    renderOption={(props, option) => {
                      if (option.id === "show_more") {
                        return (
                          <li
                            {...props}
                            onClick={async () => {
                              setShowMoreLoading(true);
                              setOffset((prev) => prev + 20);
                            }}
                            style={{
                              cursor: "pointer",
                              color: showMoreLoading ? "grey" : "#1a6b7c",
                            }}
                          >
                            {showMoreLoading ? "Loading..." : option.name}
                          </li>
                        );
                      }
                      return (
                        <li
                          {...props}
                          key={option.id}
                          className={` p-3 border-b border-b-gray-300 hover:bg-gray-200 ${
                            additionalDataLoading
                              ? "pointer-events-none opacity-25"
                              : ""
                          }`}
                        >
                          <p>{option.name}</p>
                          <div className="grid grid-cols-2 gap-1">
                            {/* SugarCRM-specific fields (Alien Number, Case Status, Lead
                                Attorney, Active Legal Assistant, Active Paralegal) removed -
                                DMS standalone now only shows local client fields below.
                            {additionalDataFetched &&
                              !searchLoading &&
                              !additionalDataLoading && (
                                <>
                                  <span className="font-semibold">Alien Number</span>
                                  <span className="break-words">
                                    : {option.im_alien_number_c}
                                  </span>
                                  <span className="font-semibold">
                                    Case Status
                                  </span>
                                  <span className="break-words ">
                                    : {option?.status}
                                  </span>

                                  <span className="font-semibold">
                                    Lead Attorney
                                  </span>
                                  <span className="break-words ">
                                    : {option?.Lead_Attorney}
                                  </span>

                                  <span className="font-semibold">
                                    Active Legal Assistant
                                  </span>
                                  <span className="break-words ">
                                    : {option?.Legal_Assistant}
                                  </span>

                                  <span className="font-semibold">
                                    Active Paralegal
                                  </span>
                                  <span className="break-words ">
                                    : {option?.Paralegal}
                                  </span>
                                </>
                              )}
                            */}
                            <span className="font-semibold">
                              Client Reference Number
                            </span>
                            <span className="break-words ">
                              : {option?.caseid_c}
                            </span>

                            <span className="font-semibold">Phone</span>
                            <span className="break-words ">
                              : {option?.phone_mobile}
                            </span>

                            <span className="font-semibold">Email</span>
                            <span className="break-words">
                              : {option?.email?.[0]?.email_address || ""}
                            </span>
                          </div>

                          {(searchLoading || additionalDataLoading) && (
                            <div className="absolute inset-0 bg-gray-50 opacity-10 flex justify-center items-center">
                              <span className="text-gray-500 font-semibold">
                                <Loading />
                              </span>
                            </div>
                          )}
                        </li>
                      );
                    }}
                    {...field}
                    value={
                      clientNames
                        ? clientNames.find((client) =>
                            client._module === "Contacts"
                              ? client.case_id === field.value
                              : client.id === field.value,
                          ) || null
                        : null
                    }
                    onChange={(event, newValue) => {
                      if (newValue === null || newValue === undefined) {
                        field.onChange(null);
                      } else if (newValue.id !== "show_more") {
                        const selectedClient = newValue;
                        let selectedId;

                        // Only "Contacts" records key off case_id — cpm_CaseParties
                        // (and every other module) only has id.
                        selectedId =
                          selectedClient._module === "Contacts"
                            ? selectedClient.case_id
                            : selectedClient.id;

                        setBenefId(selectedClient.id);
                        field.onChange(selectedId);
                      }
                    }}
                    classes={{ paper: "autocomplete-paper" }}
                  />
                )}
              />

              {/* <FormControl fullWidth>
                <InputLabel id="client-select">Client Name</InputLabel>
                <Controller
                  control={control}
                  name="addClientName"
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      name="addClientName"
                      sx={{ margin: "10px 0px" }}
                      labelId="client-select"
                      variant="outlined"
                      id="client-select"
                      label="Client Name"
                      {...field}
                      {...register("addClientName")}
                    >
                      {clientNames?.map((clientName) => (
                        <MenuItem
                          key={clientName.contact_id}
                          value={clientName.contact_id}
                        >
                          {clientName.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl> */}
              <span className="add-doc-error-message">
                {errors["addClientName"]?.message}
              </span>
            </div>
          </div>
          <div className="add-document-inputs">
            <div
              className="add-document-inputs-with-error-message"
              style={{ width: "100%" }}
            >
              <InputUI
                name="documentName"
                label="Document Name"
                type="text"
                variant="outlined"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
              />
              <span className="add-doc-error-message">
                {errors["documentName"]?.message}
              </span>
            </div>
          </div>
          <div className="add-document-inputs">
            <div
              className="add-document-inputs-with-error-message"
              style={{ width: "100%" }}
            >
              <InputUI
                name="documentDescription"
                label="Document Description"
                type="text"
                variant="outlined"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
              />
              {/* <span className="add-doc-error-message">
                {errors["documentDescription"]?.message}
              </span> */}
            </div>
          </div>
          <div className="add-document-inputs">
            <div className="add-document-inputs-with-error-message">
              <InputUI
                name="Category"
                label="Category"
                type="text"
                variant="outlined"
                disabled
                value="Manual Process"
                cssStyles={{ margin: "10px 0px" }}
                register={register}
              />
              {/* <FormControl fullWidth>
                <InputLabel id="category-select">Category</InputLabel>
                <Controller
                  control={control}
                  name="Category"
                  defaultValue="0c73a993-4dce-4430-b5a9-9d4ea57739e8"
                  render={({ field }) => (
                    <Select
                      name="Category"
                      sx={{ margin: "10px 0px" }}
                      labelId="category-select"
                      variant="outlined"
                      id="category-select"
                      label="Category"
                      disabled
                      {...field}
                      {...register("Category")}
                    >
                      {categoryOptions?.map((category) => (
                        <MenuItem
                          key={category.fields_name}
                          value={category.field_setting_id}
                        >
                          {category.fields_name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              <span className="add-doc-error-message">
                {errors["Category"]?.message}
              </span> */}
            </div>
            {/* <div className="add-document-inputs-with-error-message select-ui">
              <FormControl fullWidth>
                <InputLabel id="select-label">Status</InputLabel>
                <Controller
                  control={control}
                  name="addDocumentStatus"
                  defaultValue="active"
                  render={({ field: { onChange, value } }) => (
                    <Select
                      name="addDocumentStatus"
                      sx={{ margin: "10px 0px" }}
                      labelId="select-label"
                      variant="outlined"
                      id="simple-select"
                      value={value}
                      onChange={onChange}
                      label="Status"
                      {...register("addDocumentStatus")}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              <span className="add-doc-error-message">
                {errors["addClientStatus"]?.message}
              </span>
            </div> */}
            <div className="add-document-inputs-with-error-message">
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="subCategory"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Autocomplete
                      open={openSubCategory}
                      onOpen={() => setOpenSubCategory(true)}
                      onClose={() => setOpenSubCategory(false)}
                      id="autocomplete-client-category"
                      options={subCategoryOptions || []}
                      filterOptions={filterOptions}
                      loading={loadingSubCategory}
                      getOptionLabel={(option) => option.fields_name || ""}
                      sx={{ margin: "10px 0px" }}
                      // value={
                      //   subCategoryOptions.find(
                      //     (option) => option.field_setting_id === value
                      //   ) || null
                      // }
                      onChange={(event, newValue) => {
                        // Pass the selected value to onChange to update react-hook-form state
                        onChange(newValue ? newValue.field_setting_id : "");
                      }}
                      // onBlur={onBlur}
                      // isOptionEqualToValue={(option, value) =>
                      //   option.field_setting_id === value
                      // }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Sub Category"
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingSubCategory ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}
                />
              </FormControl>
              {/* <FormControl fullWidth>
                <InputLabel id="category-select">Sub Category</InputLabel>
                <Controller
                  control={control}
                  name="subCategory"
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      name="subCategory"
                      sx={{ margin: "10px 0px" }}
                      labelId="category-select"
                      variant="outlined"
                      id="category-select"
                      label="Sub Category"
                      {...field}
                      {...register("subCategory")}
                    >
                      {subCategoryOptions?.map((category) => (
                        <MenuItem
                          key={category.field_setting_id}
                          value={category.field_setting_id}
                        >
                          {category.fields_name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl> */}
              <span className="add-doc-error-message">
                {errors["subCategory"]?.message}
              </span>
            </div>
          </div>
          <div className="add-document-inputs">
            <div
              className="add-document-inputs-with-error-message upload-ui"
              style={{ width: "100%" }}
            >
              {/* <InputUI
                name="fileUpload"
                type="file"
                variant="outlined"
                cssStyles={{
                  margin: "10px 0px",
                }}
                register={register}
              /> */}
              <div
                {...getRootProps({ className: "dropzone" })}
                className="border-dashed border-2 border-gray-400 p-4 w-full text-center cursor-pointer h-[200px] rounded-md flex flex-col items-center justify-center"
              >
                <input
                  name="fileUpload"
                  {...getInputProps()}
                  {...register("fileUpload")}
                />
                <div className="flex flex-col justify-center items-center">
                  <Upload />
                  <p className="font-bold text-muted-foreground text-lg">
                    Drop a file here
                  </p>
                  <small className="text-base">
                    Maximum upload size: 300 MB
                  </small>{" "}
                  {/* ← add */}
                  {files?.length == 1 && (
                    <p className="text-sm text-gray-600 mt-2">
                      {files[0].name}
                    </p>
                  )}
                </div>
              </div>

              {/* ← add large file warning below dropzone */}
              {hasLargeFile && (
                <div className="w-full mt-2 p-3 bg-yellow-100 text-yellow-800 rounded-md text-center font-medium text-sm">
                  ⚠️ Large file detected (greater than 5MB). Upload may take
                  longer due to compression. Please wait...
                </div>
              )}
              {fileError && (
                <span className="add-doc-error-message">
                  {errors["fileUpload"]?.message}
                </span>
              )}
            </div>
          </div>

          <div className="add-document-inputs">
            {!linkRequest &&
            files?.length > 0 &&
            filteredRequestOptions?.length > 0 ? (
              <div className="add-document-inputs-with-error-message select-ui">
                <span
                  className="request-list-link"
                  onClick={handleRequestListLink}
                >
                  Link to Request List
                </span>
              </div>
            ) : null}

            {linkRequest ? (
              <div className="add-document-inputs-with-error-message">
                <FormControl fullWidth>
                  <Controller
                    control={control}
                    name="addRequestName"
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        options={filteredRequestOptions}
                        getOptionLabel={(option) =>
                          option.document_req_name || ""
                        }
                        filterOptions={filterOptionsLinkList}
                        onChange={(e, value) => {
                          onChange(value ? value.document_request_id : "");
                        }}
                        value={
                          filteredRequestOptions?.find(
                            (option) => option.document_request_id === value,
                          ) || null
                        }
                        sx={{ margin: "10px 0px" }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            key={option.document_request_id}
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            {option.is_present === 1 &&
                              isFileValidForLinking &&
                              normalizeExt(option.document_type || "") ===
                                fileExtension && (
                                <BiLinkAlt
                                  style={{
                                    color: "#1976d2",
                                    fontSize: 20,
                                    marginRight: "8px",
                                  }}
                                />
                              )}
                            <span style={{ flex: 1 }}>
                              {option.document_req_name}
                            </span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Request List"
                            variant="outlined"
                            sx={{ margin: "10px 0px" }}
                          />
                        )}
                      />
                    )}
                  />
                </FormControl>
              </div>
            ) : null}
          </div>

          <div
            className="add-document-button"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <button
              type="button"
              disabled={loading}
              onClick={handleSubmit(onAddDocumentFormSubmit)}
              style={{
                width: "150px",
                height: "35px",
                backgroundColor: "#1a6b7c",
                border: "1px solid #1a6b7c",
                borderRadius: "4px",
                marginTop: "20px",
                color: "white",
                fontWeight: "500",
                letterSpacing: "0.07rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Uploading...
                </>
              ) : (
                "Add Document"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDocument;
